import React, { useEffect, useState } from "react"
import {
    View,
    StyleSheet,
    ScrollView,
    TouchableOpacity,
    ActivityIndicator,
} from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import FilledButtonM from "@common/components/button/FilledButtonM"
import TextM from "@common/components/text/TextM"
import { ModalSkeleton } from "@common/modals/ModalSkeleton"
import { theme } from "@common/theme/theme"
import { stageFunnelService } from "../services/stageFunnelService"

/**
 * Sélecteur d'un chapitre pour une séance — remplacement ou ajout.
 *
 * Reprend le squelette de modale de l'app ({@see ModalSkeleton}) et la
 * mécanique de copie de travail de {@see StageSubjectPickerModal} : rien n'est
 * appliqué avant « Valider ».
 *
 * Déviation assumée du motif matières : une LISTE de lignes plutôt qu'une
 * grille de pastilles ({@see ClassChipGridM}). Un chapitre porte un intitulé
 * long et son thème, qu'il faut lire pour choisir — une pastille les
 * tronquerait.
 *
 * Les chapitres proposés sont restreints à la matière de la séance : le parent
 * ne peut pas glisser une matière qu'il n'a pas payée. Le backend refuse la
 * même chose ; l'écran ne la propose simplement pas.
 */
const StageChapterPickerModal = ({
    isVisible,
    onClose,
    studentClass,
    subjectId,
    subjectName,
    slotLabel,
    /** Chapitre en cours de remplacement — null quand on ajoute. */
    currentChapterId = null,
    /** Chapitres déjà posés sur la séance : non re-proposables. */
    takenChapterIds = [],
    onValidate,
}) => {
    const [staged, setStaged] = useState(currentChapterId)
    const [chapters, setChapters] = useState([])
    const [loading, setLoading] = useState(false)
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        if (!isVisible) return
        setStaged(currentChapterId)
        if (!subjectId || !studentClass) return
        let cancelled = false
        setLoading(true)
        setFailed(false)
        stageFunnelService
            .getStageChapters({ studentClass, schoolSubjectId: subjectId })
            .then((res) => {
                if (cancelled) return
                const payload = res?.data?.data ?? res?.data ?? {}
                setChapters(payload.chapters ?? [])
            })
            .catch(() => {
                if (!cancelled) setFailed(true)
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, subjectId, studentClass])

    // Les chapitres se lisent par domaine (le thème du référentiel : « Nombres
    // et calculs », « Géométrie »…) : une section par domaine, dans l'ordre où
    // le référentiel les sert, plutôt qu'une liste plate où le domaine se
    // répétait sous chaque ligne.
    const sections = []
    const options = chapters.filter(
        (chapter) =>
            chapter.id === currentChapterId ||
            !takenChapterIds.includes(chapter.id)
    )
    options.forEach((chapter) => {
        const theme_ = chapter.theme || ""
        const last = sections.find((section) => section.theme === theme_)
        if (last) last.chapters.push(chapter)
        else sections.push({ theme: theme_, chapters: [chapter] })
    })

    return (
        <ModalSkeleton
            isVisible={isVisible}
            onClose={onClose}
            title={
                currentChapterId ? "Changer le chapitre" : "Ajouter un chapitre"
            }
        >
            <View>
                {slotLabel ? (
                    <TextM
                        fontWeight="Bold"
                        fontSize={12}
                        color={theme.colors.primary}
                        wrap
                        style={styles.slotLabel}
                    >
                        {slotLabel}
                    </TextM>
                ) : null}
                <TextM
                    fontSize={12}
                    color={theme.colors.grey}
                    wrap
                    style={styles.hint}
                >
                    {subjectName
                        ? `Chapitres de ${subjectName} au programme de la classe de votre enfant.`
                        : "Chapitres au programme."}
                </TextM>

                {loading ? (
                    <View style={styles.loading}>
                        <ActivityIndicator color={theme.colors.primary} />
                    </View>
                ) : failed ? (
                    <TextM
                        fontSize={13}
                        color={theme.colors.grey}
                        wrap
                        style={styles.empty}
                    >
                        Impossible de charger les chapitres. Vérifiez votre
                        connexion puis réessayez.
                    </TextM>
                ) : options.length === 0 ? (
                    <TextM
                        fontSize={13}
                        color={theme.colors.grey}
                        wrap
                        style={styles.empty}
                    >
                        Tous les chapitres de cette matière sont déjà placés sur
                        cette séance.
                    </TextM>
                ) : (
                    <ScrollView
                        style={styles.list}
                        showsVerticalScrollIndicator={false}
                    >
                        {sections.map((section) => (
                            <View key={section.theme || "_"}>
                                {section.theme ? (
                                    <TextM
                                        fontWeight="Bold"
                                        fontSize={11}
                                        color={theme.colors.grey}
                                        wrap
                                        style={styles.sectionTitle}
                                    >
                                        {section.theme}
                                    </TextM>
                                ) : null}
                                {section.chapters.map((chapter) => {
                                    const selected = staged === chapter.id
                                    return (
                                        <TouchableOpacity
                                            key={chapter.id}
                                            activeOpacity={0.8}
                                            onPress={() =>
                                                setStaged(chapter.id)
                                            }
                                            accessibilityRole="radio"
                                            accessibilityState={{
                                                checked: selected,
                                            }}
                                            style={[
                                                styles.row,
                                                selected && styles.rowSelected,
                                            ]}
                                        >
                                            <FontAwesome5
                                                name={
                                                    selected
                                                        ? "dot-circle"
                                                        : "circle"
                                                }
                                                solid={selected}
                                                size={16}
                                                color={
                                                    selected
                                                        ? theme.colors.primary
                                                        : theme.colors.lightMore
                                                }
                                            />
                                            <View style={styles.rowTexts}>
                                                <TextM
                                                    fontWeight="Bold"
                                                    fontSize={13}
                                                    color={
                                                        selected
                                                            ? theme.colors
                                                                  .primary
                                                            : theme.colors.grey
                                                    }
                                                    wrap
                                                >
                                                    {chapter.name}
                                                </TextM>
                                            </View>
                                        </TouchableOpacity>
                                    )
                                })}
                            </View>
                        ))}
                    </ScrollView>
                )}

                <FilledButtonM
                    label="Valider"
                    onPress={() => onValidate?.(staged)}
                    disabled={!staged}
                    color={
                        staged ? theme.colors.primary : theme.colors.lightMore
                    }
                    style={styles.validate}
                />
            </View>
        </ModalSkeleton>
    )
}

const styles = StyleSheet.create({
    slotLabel: { marginBottom: 6 },
    hint: { marginBottom: 14, lineHeight: 16 },
    sectionTitle: {
        textTransform: "uppercase",
        letterSpacing: 0.5,
        marginTop: 10,
        marginBottom: 6,
    },
    loading: { paddingVertical: 24, alignItems: "center" },
    empty: { paddingVertical: 12, lineHeight: 18 },
    list: { maxHeight: 320 },
    row: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 10,
        paddingVertical: 11,
        paddingHorizontal: 12,
        marginBottom: 8,
        borderRadius: 12,
        borderWidth: 1.5,
        borderColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
    rowSelected: {
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.primaryLighter,
    },
    rowTexts: { flex: 1 },
    rowTheme: { marginTop: 2 },
    validate: { width: "100%", marginTop: 18, marginBottom: 0 },
})

export default StageChapterPickerModal
